<template>
  <div id="wrapper">
    <LazyHomePopupVideo
        v-if="isShowVideo"
        @on-click-close="emit('update:isShowVideo', false)"/>
    <div class="headerWrap" :class="headerScrollClass">
      <LazyUserCommonCreditBanner
          v-if="isOpenCreditBanner"
          @close-banner="creditCookieOpen = false"/>
<!--      <LazyHomeCommonBlogBanner v-if="isBlogBanner" />-->
      <LazyHomeHeaderMain
          :menu-list="headerMenuList"
          :is-login-user="isLoginUser"
          @on-click-menu="methods.onClickMenu"
          @on-click-sub-menu="methods.onClickSubMenu"
          @reset-menu-list="methods.resetMenuList"
      />
    </div>
    <div class="backBgCircle">
      <div class="circleWrap">
        <div class="circle"></div>
      </div>
    </div>
    <slot name="content" />
    <LazyHomeFooterMain
        :menu-list="footerMenuList"
        @on-click-menu="methods.onClickMenu"
    />
    <LazyHomeCommonButtonBackToTop />
    <div class="navBg"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "#imports";
import {
  homeMainFooterMenu,
  homeMainHeaderMenu,
} from "~~/package/config/HomeSubMenuConfig";
import { useUserStore } from "~/store/user/userStore";
import { CookieKeyType } from "~~/package/mapper/common/model/CookieKeyType";
import { getCookie } from "~~/package/utils/CookieUtil";

const props = defineProps({
  isShowVideo: { type: Boolean, default: false },
  isBlogBanner: { type: Boolean, default: false },
});
const emit = defineEmits(["update:isShowVideo"]);

const userStore = useUserStore();
// Reactive — was previously `ref(false)` + one-time onMounted snapshot which
// missed late-arriving OAuth state and caused the anon→logged-in flash.
const isLoginUser = computed(() => userStore.isLogin());
const headerMenuList = ref(homeMainHeaderMenu);
const footerMenuList = ref(homeMainFooterMenu);

const creditCookieOpen = ref(getCookie(CookieKeyType.OPEN_CREDIT_BANNER));

const isOpenCreditBanner = computed(() => {
  return (userStore.isAdminType || userStore.isSuperAdminType) &&
    creditCookieOpen.value &&
    userStore.userV2.credit <= 1
})

const lastScrollY = ref(0);
const scrollHidden = ref(false);
const scrollOn = ref(false);
const headerScrollClass = computed(() => ({ hidden: scrollHidden.value, on: scrollOn.value }));

const onScroll = () => {
  const currentScrollY = window.scrollY;
  scrollHidden.value = currentScrollY > lastScrollY.value && currentScrollY > 100;
  scrollOn.value = currentScrollY > 0;
  lastScrollY.value = currentScrollY;
};

onMounted(() => {
  lastScrollY.value = window.scrollY;
  window.addEventListener('scroll', onScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
});

const methods = {
  onClickMenu(menuTitle: string, option: string) {
    if (option === "header") {
      headerMenuList.value.forEach((menu, index) => {
        headerMenuList.value[index].openFlag = menu.title === menuTitle
      });
    } else {
      footerMenuList.value.forEach((menu, index) => {
        if (menu.title === menuTitle) {
          footerMenuList.value[index].openFlag = !footerMenuList.value[index].openFlag;
        }
      });
    }
  },
  onClickSubMenu(subMenuTitle: string) {
    headerMenuList.value.forEach((menu, index) => {
      headerMenuList.value[index].openFlag = false;
      if (!menu.subTitle.length && menu.title.toLowerCase() === subMenuTitle.toLowerCase()) {
        headerMenuList.value[index].openFlag = true;
        return;
      }
      menu.subTitle.forEach((subMenu, subIdx) => {
        if (subMenu.title.toLowerCase().includes(subMenuTitle.toLowerCase())) {
          headerMenuList.value[index].openFlag = true;
          headerMenuList.value[index].subTitle[subIdx].openFlag = true;
        } else {
          headerMenuList.value[index].subTitle[subIdx].openFlag = false;
        }
      });
    });
  },
  resetMenuList() {
    headerMenuList.value.forEach((menu, index) => {
      headerMenuList.value[index].openFlag = false;
      menu.subTitle.forEach((subMenu, subIdx) => {
        headerMenuList.value[index].subTitle[subIdx].openFlag = false;
      });
    });

    footerMenuList.value.forEach((menu, index) => {
      footerMenuList.value[index].openFlag = false;
    });
  }
};
</script>

<style lang="scss">
@import "~/assets/scss/abstracts/variables.scss";
@import "~/assets/scss/abstracts/mixin.scss";
@import "~/assets/scss/input.scss";
@import "~/assets/scss/sub.scss";
</style>